import React, { useCallback } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { WorkflowCandidateResumeScoreWriteBackV2Config } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { WorkflowCandidateResumeScoreWriteBackV2ConfigOption } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { WriteBackOverrideRule } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { evaluateReadinessBasedOnOverrideRules } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { checkIfRuleMatches } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { determineCandidateResumeApplicantConfig } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { ConfigureWriteBackLeverStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackLeverStrategy';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { COMPARATOR_LABELS } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackOverrideRule';
import { ConfigureOverrideRule } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackOverrideRule';
import { DEFAULT_OVERRIDE_RULE } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackOverrideRule';

export interface ConfigureCandidateResumeScoreWriteBackV2ConfigProps
  extends ConfigureWorkflowBaseProps {
  v2Config: WorkflowCandidateResumeScoreWriteBackV2Config | null;
  updateV2Config: (
    v2Config: WorkflowCandidateResumeScoreWriteBackV2Config | null,
  ) => void;
  legacyStrategy?: WorkflowCandidateResumeScoreWriteBackV2ConfigOption['strategy'];
  legacyMode?: WorkflowWriteBackMode;
}

export const ConfigureCandidateResumeScoreWriteBackV2Config = ({
  v2Config,
  updateV2Config,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
  legacyStrategy,
  legacyMode,
}: ConfigureCandidateResumeScoreWriteBackV2ConfigProps) => {
  const toggleV2ConfigEnabled = useCallback(() => {
    updateV2Config(
      v2Config
        ? null
        : {
            options: [],
          },
    );
  }, [v2Config, updateV2Config]);

  const setOptions = useCallback(
    (options: WorkflowCandidateResumeScoreWriteBackV2ConfigOption[]) => {
      if (!v2Config) return;
      updateV2Config({
        ...v2Config,
        options,
      });
    },
    [v2Config, updateV2Config],
  );

  const optionsCrud = useCrudOperations({
    items: v2Config?.options ?? [],
    setItems: setOptions,
  });

  const addNewOption = useCallback(() => {
    optionsCrud.onAdd({
      groupName: 'New Group',
      strategy: undefined,
      mode: 'normal',
      overrideRules: [],
    });
  }, [optionsCrud]);

  const toggleNoGroupConfig = useCallback(() => {
    if (!v2Config) return;
    updateV2Config({
      ...v2Config,
      noGroupConfig: v2Config.noGroupConfig
        ? undefined
        : {
            strategy: undefined,
            mode: 'normal',
            overrideRules: [],
          },
    });
  }, [v2Config, updateV2Config]);

  const updateNoGroupConfig = useCallback(
    (
      partial: Partial<
        Omit<WorkflowCandidateResumeScoreWriteBackV2ConfigOption, 'groupName'>
      >,
    ) => {
      if (!v2Config || !v2Config.noGroupConfig) return;
      updateV2Config({
        ...v2Config,
        noGroupConfig: {
          ...v2Config.noGroupConfig,
          ...partial,
        },
      });
    },
    [v2Config, updateV2Config],
  );

  return (
    <div className="space-y-6">
      <ResumeScoreTester
        v2Config={v2Config}
        legacyStrategy={legacyStrategy}
        legacyMode={legacyMode}
      />

      <div className="border-t pt-6">
        <S2Checkbox
          checked={Boolean(v2Config)}
          onChange={toggleV2ConfigEnabled}
        >
          Enable V2 Configuration (Group-based write-back with override rules)
        </S2Checkbox>
      </div>

      {v2Config && (
        <div className="space-y-6 pl-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Group Configurations</h3>
            <Button
              behavior={{
                type: 'onClick',
                onClick: addNewOption,
              }}
              text="+ Add Group"
              size="small"
              variant="primary"
            />
          </div>

          {v2Config.options.length === 0 && (
            <div className="text-gray-500 italic">
              No groups configured. Click Add Group to create one.
            </div>
          )}

          {v2Config.options.map((option, optionIndex) => (
            <ConfigureV2ConfigOption
              key={optionIndex}
              option={option}
              optionIndex={optionIndex}
              onEdit={optionsCrud.onEdit}
              onDelete={optionsCrud.onDelete}
              listSecretsPattern={listSecretsPattern}
              organizationId={organizationId}
              listDataSetsPattern={listDataSetsPattern}
            />
          ))}

          <div className="border-t pt-6 space-y-4">
            <S2Checkbox
              checked={Boolean(v2Config.noGroupConfig)}
              onChange={toggleNoGroupConfig}
            >
              Enable No-Group Fallback Configuration
            </S2Checkbox>
            <p className="text-sm text-gray-600 pl-6">
              Applicants without a group or with an empty group will use this
              configuration. If disabled, applicants without groups will be
              skipped.
            </p>

            {v2Config.noGroupConfig && (
              <ConfigureNoGroupConfig
                noGroupConfig={v2Config.noGroupConfig}
                updateNoGroupConfig={updateNoGroupConfig}
                listSecretsPattern={listSecretsPattern}
                organizationId={organizationId}
                listDataSetsPattern={listDataSetsPattern}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface ConfigureNoGroupConfigProps extends ConfigureWorkflowBaseProps {
  noGroupConfig: Omit<
    WorkflowCandidateResumeScoreWriteBackV2ConfigOption,
    'groupName'
  >;
  updateNoGroupConfig: (
    partial: Partial<
      Omit<WorkflowCandidateResumeScoreWriteBackV2ConfigOption, 'groupName'>
    >,
  ) => void;
}

const ConfigureNoGroupConfig = ({
  noGroupConfig,
  updateNoGroupConfig,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureNoGroupConfigProps) => {
  const updateStrategy = useCallback(
    (
      strategy:
        | WorkflowCandidateResumeScoreWriteBackV2ConfigOption['strategy']
        | null,
    ) => {
      updateNoGroupConfig({ strategy: strategy ?? undefined });
    },
    [updateNoGroupConfig],
  );

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateNoGroupConfig({ mode });
    },
    [updateNoGroupConfig],
  );

  const setOverrideRules = useCallback(
    (overrideRules: WriteBackOverrideRule[]) => {
      updateNoGroupConfig({ overrideRules });
    },
    [updateNoGroupConfig],
  );

  const rulesCrud = useCrudOperations({
    items: noGroupConfig.overrideRules ?? [],
    setItems: setOverrideRules,
  });

  const addNewRule = useCallback(() => {
    rulesCrud.onAdd(DEFAULT_OVERRIDE_RULE);
  }, [rulesCrud]);

  return (
    <div className="border rounded-lg p-6 space-y-6 bg-blue-50 ml-6">
      <h4 className="text-md font-semibold">No-Group Fallback Configuration</h4>

      <ConfigureWriteBackLeverStrategy
        strategy={noGroupConfig.strategy ?? null}
        updateStrategy={updateStrategy}
        listSecretsPattern={listSecretsPattern}
        organizationId={organizationId}
        listDataSetsPattern={listDataSetsPattern}
      />

      <ConfigureWriteBackMode
        mode={noGroupConfig.mode ?? null}
        updateMode={updateMode}
      />

      <div className="border-t pt-4 space-y-4">
        <div className="flex justify-between items-center">
          <h5 className="font-semibold">Override Rules</h5>
          <Button
            behavior={{
              type: 'onClick',
              onClick: addNewRule,
            }}
            text="+ Add Rule"
            size="small"
            variant="outline"
          />
        </div>

        {(!noGroupConfig.overrideRules ||
          noGroupConfig.overrideRules.length === 0) && (
          <div className="text-gray-500 italic text-sm">
            No override rules. Resume scores will be written back as-is.
          </div>
        )}

        {(noGroupConfig.overrideRules ?? []).map((rule, ruleIndex) => (
          <ConfigureOverrideRule
            key={ruleIndex}
            rule={rule}
            ruleIndex={ruleIndex}
            onEdit={rulesCrud.onEdit}
            onDelete={rulesCrud.onDelete}
          />
        ))}
      </div>
    </div>
  );
};

interface ConfigureV2ConfigOptionProps extends ConfigureWorkflowBaseProps {
  option: WorkflowCandidateResumeScoreWriteBackV2ConfigOption;
  optionIndex: number;
  onEdit: (
    index: number,
    item: Partial<WorkflowCandidateResumeScoreWriteBackV2ConfigOption>,
  ) => void;
  onDelete: (index: number) => void;
}

const ConfigureV2ConfigOption = ({
  option,
  optionIndex,
  onEdit,
  onDelete,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureV2ConfigOptionProps) => {
  const updateOption = useCallback(
    (partial: Partial<WorkflowCandidateResumeScoreWriteBackV2ConfigOption>) => {
      onEdit(optionIndex, partial);
    },
    [onEdit, optionIndex],
  );

  const updateGroupName = useCallback(
    (groupName: string) => {
      updateOption({ groupName });
    },
    [updateOption],
  );

  const updateStrategy = useCallback(
    (
      strategy:
        | WorkflowCandidateResumeScoreWriteBackV2ConfigOption['strategy']
        | null,
    ) => {
      updateOption({ strategy: strategy ?? undefined });
    },
    [updateOption],
  );

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateOption({ mode });
    },
    [updateOption],
  );

  const setOverrideRules = useCallback(
    (overrideRules: WriteBackOverrideRule[]) => {
      updateOption({ overrideRules });
    },
    [updateOption],
  );

  const rulesCrud = useCrudOperations({
    items: option.overrideRules ?? [],
    setItems: setOverrideRules,
  });

  const addNewRule = useCallback(() => {
    rulesCrud.onAdd(DEFAULT_OVERRIDE_RULE);
  }, [rulesCrud]);

  const deleteOption = useCallback(() => {
    onDelete(optionIndex);
  }, [onDelete, optionIndex]);

  return (
    <div className="border rounded-lg p-6 space-y-6 bg-gray-50">
      <div className="flex justify-between items-start">
        <h4 className="text-md font-semibold">Group Configuration</h4>
        <Button
          behavior={{
            type: 'onClick',
            onClick: deleteOption,
          }}
          text="Delete Group"
          size="small"
          variant="destructive"
        />
      </div>

      <label className="space-y-1 flex flex-col">
        <span className="font-medium">Group Name</span>
        <input
          type="text"
          value={option.groupName}
          onChange={(e) => {
            updateGroupName(e.target.value);
          }}
          className="form-input border rounded p-2"
          placeholder="e.g., Engineering, Sales, Marketing"
        />
      </label>

      <ConfigureWriteBackLeverStrategy
        strategy={option.strategy ?? null}
        updateStrategy={updateStrategy}
        listSecretsPattern={listSecretsPattern}
        organizationId={organizationId}
        listDataSetsPattern={listDataSetsPattern}
      />

      <ConfigureWriteBackMode
        mode={option.mode ?? null}
        updateMode={updateMode}
      />

      <div className="border-t pt-4 space-y-4">
        <div className="flex justify-between items-center">
          <h5 className="font-semibold">Override Rules</h5>
          <Button
            behavior={{
              type: 'onClick',
              onClick: addNewRule,
            }}
            text="+ Add Rule"
            size="small"
            variant="outline"
          />
        </div>

        {(!option.overrideRules || option.overrideRules.length === 0) && (
          <div className="text-gray-500 italic text-sm">
            No override rules. Resume scores will be written back as-is.
          </div>
        )}

        {(option.overrideRules ?? []).map((rule, ruleIndex) => (
          <ConfigureOverrideRule
            key={ruleIndex}
            rule={rule}
            ruleIndex={ruleIndex}
            onEdit={rulesCrud.onEdit}
            onDelete={rulesCrud.onDelete}
          />
        ))}
      </div>
    </div>
  );
};

interface ResumeScoreTesterProps {
  v2Config: WorkflowCandidateResumeScoreWriteBackV2Config | null;
  legacyStrategy?: WorkflowCandidateResumeScoreWriteBackV2ConfigOption['strategy'];
  legacyMode?: WorkflowWriteBackMode;
}

type ResumeTestResult =
  | {
      status: 'success' | 'no-group-fallback';
      groupName: string;
      strategy: { type: string };
      mode: WorkflowWriteBackMode;
      originalScore: number;
      evaluatedValue: string | number;
      overrideRules: WriteBackOverrideRule[];
    }
  | { status: 'skipped'; reason: string }
  | { status: 'error'; reason: string };

const ResumeScoreTesterResult = ({
  testResult,
  resumeScore,
}: {
  testResult: ResumeTestResult;
  resumeScore: number;
}) => {
  if (
    testResult.status === 'success' ||
    testResult.status === 'no-group-fallback'
  ) {
    return (
      <div className="bg-green-50 border border-green-200 rounded p-4 space-y-3">
        <div className="flex items-start gap-2">
          <span className="text-green-600 font-bold text-lg">✓</span>
          <div className="flex-1 space-y-2">
            <p className="font-semibold text-green-900">
              {testResult.status === 'no-group-fallback'
                ? 'Using No-Group Fallback Configuration'
                : testResult.groupName === '(Legacy Configuration)'
                  ? 'Using Legacy (V1) Configuration'
                  : 'Configuration Found'}
            </p>
            <div className="text-sm space-y-1">
              {testResult.groupName !== '(Legacy Configuration)' && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Group:</span>
                  <span className="font-medium">{testResult.groupName}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Strategy Type:</span>
                <span className="font-medium">{testResult.strategy.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Write-Back Mode:</span>
                <span className="font-medium">{testResult.mode}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Original Score:</span>
                  <span className="font-mono font-bold text-lg">
                    {testResult.originalScore}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-gray-600">Value to Write Back:</span>
                  <span className="font-mono font-bold text-lg text-green-600">
                    {String(testResult.evaluatedValue)}
                  </span>
                </div>
                {testResult.originalScore !== testResult.evaluatedValue && (
                  <div className="text-xs text-orange-600 mt-1 bg-orange-50 p-2 rounded">
                    Score was overridden by a matching rule
                  </div>
                )}
              </div>
              {testResult.overrideRules.length > 0 && (
                <div className="border-t pt-2 mt-2">
                  <span className="text-gray-600 text-xs">
                    Override Rules Applied:
                  </span>
                  <div className="mt-1 space-y-1">
                    {testResult.overrideRules.map((rule, idx) => {
                      const matches = checkIfRuleMatches({
                        readinessRating: resumeScore,
                        rule,
                      });
                      return (
                        <div
                          key={idx}
                          className={`text-xs p-2 rounded ${
                            matches
                              ? 'bg-green-100 border border-green-300'
                              : 'bg-gray-100 border border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-1">
                            <span>{matches ? '✓' : '○'}</span>
                            <span className="font-medium">Rule {idx + 1}:</span>
                            {rule.conditions.map((cond, condIdx) => (
                              <span key={condIdx}>
                                {condIdx > 0 && ' AND '}
                                {COMPARATOR_LABELS[cond.comparator]}{' '}
                                {cond.thresholdValue}
                              </span>
                            ))}
                            <span className="ml-1">
                              → {String(rule.overrideValue)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (testResult.status === 'skipped') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
        <div className="flex items-start gap-2">
          <span className="text-yellow-600 font-bold text-lg">⚠</span>
          <div>
            <p className="font-semibold text-yellow-900">
              Applicant Would Be Skipped
            </p>
            <p className="text-sm text-gray-700 mt-1">{testResult.reason}</p>
          </div>
        </div>
      </div>
    );
  }

  if (testResult.status === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4">
        <div className="flex items-start gap-2">
          <span className="text-red-600 font-bold text-lg">✗</span>
          <div>
            <p className="font-semibold text-red-900">Error</p>
            <p className="text-sm text-gray-700 mt-1">{testResult.reason}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const ResumeScoreTester = ({
  v2Config,
  legacyStrategy,
  legacyMode,
}: ResumeScoreTesterProps) => {
  const [groupName, setGroupName] = React.useState('');
  const [resumeScore, setResumeScore] = React.useState<number>(4);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const testResult = React.useMemo((): ResumeTestResult => {
    const config = determineCandidateResumeApplicantConfig({
      groupName,
      v2Config: v2Config ?? undefined,
      legacyStrategy,
      legacyMode,
    });

    if (config.type === 'success') {
      const evaluatedValue = evaluateReadinessBasedOnOverrideRules({
        readinessRating: resumeScore,
        overrideRules: config.overrideRules,
      });

      return {
        status: config.isNoGroupFallback ? 'no-group-fallback' : 'success',
        groupName: config.groupName,
        strategy: config.strategy,
        mode: config.mode,
        originalScore: resumeScore,
        evaluatedValue,
        overrideRules: config.overrideRules,
      };
    }

    if (config.type === 'error') {
      return { status: 'error', reason: config.reason };
    }

    return { status: 'skipped', reason: config.reason };
  }, [groupName, resumeScore, v2Config, legacyStrategy, legacyMode]);

  return (
    <div className="border-2 border-teal-200 rounded-lg p-6 space-y-4 bg-teal-50">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-teal-900">
          Resume Score Tester
        </h3>
        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              setIsExpanded(!isExpanded);
            },
          }}
          text={isExpanded ? 'Collapse' : 'Expand'}
          size="small"
          variant="outline"
        />
      </div>

      {isExpanded && (
        <>
          <p className="text-sm text-gray-700">
            Test how your configuration will process applicants. Works with both
            V1 (legacy) and V2 (group-based) configurations.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <label className="space-y-1 flex flex-col">
              <span className="font-medium text-sm">Group Name</span>
              <input
                type="text"
                value={groupName}
                onChange={(e) => {
                  setGroupName(e.target.value);
                }}
                className="form-input border rounded p-2"
                placeholder="e.g., Engineering (or leave empty)"
              />
              <span className="text-xs text-gray-500">
                {v2Config
                  ? 'Leave empty to test no-group fallback'
                  : 'N/A for V1 (legacy) config'}
              </span>
            </label>

            <label className="space-y-1 flex flex-col">
              <span className="font-medium text-sm">Resume Score</span>
              <input
                type="number"
                value={resumeScore}
                onChange={(e) => {
                  setResumeScore(e.target.valueAsNumber || 0);
                }}
                className="form-input border rounded p-2"
                placeholder="e.g., 4"
                min="0"
                max="100"
              />
              <span className="text-xs text-gray-500">
                Enter a score between 0-5
              </span>
            </label>
          </div>

          <div className="border-t pt-4 space-y-3">
            <h4 className="font-semibold text-sm">Test Results:</h4>
            <ResumeScoreTesterResult
              testResult={testResult}
              resumeScore={resumeScore}
            />
          </div>
        </>
      )}
    </div>
  );
};
